import React, { useState } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import { GetAccess, GetRole, CheckEditAction, CheckDeleteAction } from "../../../../Components/Utils/roles";
import { useDeleteData } from "../../../../Components/Hooks/useDeleteData";
import ModalContainer from "../../../../Components/Others/ModalContainer";
import PremiumTable, { CopyButton } from "../../../../Components/Others/PremiumTable";
import { Divider } from "../../Payment/Payables/PayablesTable";

// Equipment status/left border color themes helper
const getEquipmentStatusTheme = (row) => {
  const s = String(row.status || "").trim().toLowerCase();
  if (s === "active" || s === "operational" || s === "working" || s === "good") return "green";
  if (s.includes("maintenance") || s.includes("repair") || s.includes("calibration")) return "blue";
  if (s === "inactive" || s === "broken" || s === "damaged") return "red";

  // Check calibration expiration as a fallback
  if (row.calibExpDate) {
    const isExpired = moment(row.calibExpDate).isBefore(moment());
    if (isExpired) return "red";
  }
  return "purple";
};

const renderEquipmentStatus = (row) => {
  const theme = getEquipmentStatusTheme(row);
  const status = row.status || (row.calibExpDate && moment(row.calibExpDate).isBefore(moment()) ? "Calib Expired" : "Operational");

  const themes = {
    green: "bg-emerald-50 text-emerald-700 border-emerald-200/50 shadow-sm",
    blue: "bg-blue-50 text-blue-700 border-blue-200/50 shadow-sm",
    red: "bg-rose-50 text-rose-700 border-rose-200/50 shadow-sm",
    purple: "bg-indigo-50 text-indigo-700 border-indigo-200/50 shadow-sm",
  };

  const colorClass = themes[theme] || themes.purple;

  return (
    <span className={`inline-flex items-center text-center justify-center px-3.5 py-1 rounded-full text-[11px] font-bold border leading-snug tracking-wide max-w-[170px] ${colorClass}`}>
      {status}
    </span>
  );
};

const renderDate = (date) => {
  if (!date) return <span className="text-gray-400 font-semibold">-</span>;
  return (
    <span className="text-xs font-semibold text-gray-700">
      {moment(date).format("DD-MMM-YYYY")}
    </span>
  );
};

// Equipment Table Row Component
const EquipmentTableRow = ({
  row,
  pageIndex,
  searchQuery,
  fetcher,
  index,
}) => {
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const { handle: handleDeleteData } = useDeleteData(fetcher);

  const canEdit = GetAccess("equipment_edit") || GetRole();
  const canDelete = GetAccess("equipment_delete") || GetRole();

  const bgClass = "bg-white";
  const hoverClass = "hover:bg-white/80";

  const handleConfirmDelete = () => {
    handleDeleteData(`/equipments/${row.equipmentId}`, setDeleteConfirmOpen);
  };

  return (
    <>
      <tr className={`${bgClass} ${hoverClass} transition-colors align-middle`}>
        {/* ID */}
        <td className="p-4 align-middle border-t border-b border-l border-gray-200/60 rounded-tl-xl rounded-bl-xl text-xs font-semibold text-gray-500">
          <div className="flex items-center">
            <span>{row.equipmentId || "N/A"}</span>
            <CopyButton text={row.equipmentId} />
          </div>
        </td>

        {/* Type / Name */}
        <td className="p-4 align-middle border-t border-b border-gray-200/60">
          <div className="flex items-center text-base font-semibold text-blue-600">
            <Link
              to={`/maintenance/equipment/view/${row.equipmentId}`}
              state={{ data: row, pageIndex, searchQuery }}
              className="hover:underline text-base text-gray-800"
            >
              {row.equipmentType || "N/A"}
            </Link>
            <CopyButton text={row.equipmentType} />
          </div>
          {row.manufacturer && (
            <div className="text-[10px] text-gray-400 font-semibold mt-0.5">
              Manufacturer: {row.manufacturer}
            </div>
          )}
        </td>

        {/* Brand */}
        <td className="p-4 align-middle border-t border-b border-gray-200/60">
          <span className="text-xs font-semibold text-gray-700">
            {row.brand || "N/A"}
          </span>
          {row.quantity && (
            <div className="text-[10px] text-gray-400 font-semibold mt-0.5">
              Qty: {row.quantity} {row.unit || ""}
            </div>
          )}
        </td>

        {/* Action Buttons */}
        <td className="p-4 text-center align-middle border-t border-b border-r border-gray-200/60 rounded-tr-xl rounded-br-xl">
          <div className="flex items-start justify-center gap-1.5">
            {/* View */}
            <Link
              to={`/maintenance/equipment/view/${row.equipmentId}`}
              state={{ data: row, pageIndex, searchQuery }}
              className="border border-blue-200 text-blue-600 hover:bg-blue-50 p-1.5 rounded-md transition-all duration-200 flex items-center justify-center"
              title="View details"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </Link>

            {/* Edit */}
            {CheckEditAction(row, "Equipment", canEdit) ? (
              <Link
                to={`/maintenance/equipment/edit/${row.equipmentId}`}
                state={{ data: row, from: "/maintenance/equipment", pageIndex, searchQuery }}
                className="border border-sky-200 text-[#0ea5e9] hover:bg-sky-50 p-1.5 rounded-md transition-all duration-200 flex items-center justify-center"
                title="Edit Equipment"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-5M18.5 2.5a2.121 2.121 0 113 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              </Link>
            ) : null}

            {/* Delete */}
            {CheckDeleteAction(row, canDelete) ? (
              <button
                onClick={() => setDeleteConfirmOpen(true)}
                className="border border-red-200 text-red-500 hover:bg-red-50 p-1.5 rounded-md transition-all duration-200 flex items-center justify-center"
                title="Delete Equipment"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            ) : null}
          </div>
        </td>
      </tr>

      {/* Delete Warning Modal */}
      {deleteConfirmOpen && (
        <ModalContainer
          visiable={deleteConfirmOpen}
          title="Warning"
          content={`Are you sure you want to remove this Equipment record?`}
          handleClick={handleConfirmDelete}
          closeModal={() => setDeleteConfirmOpen(false)}
        />
      )}
    </>
  );
};

// Equipment Card Component
const EquipmentCard = ({
  row,
  pageIndex,
  searchQuery,
  fetcher,
  index,
}) => {
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const { handle: handleDeleteData } = useDeleteData(fetcher);

  const canEdit = GetAccess("equipment_edit") || GetRole();
  const canDelete = GetAccess("equipment_delete") || GetRole();

  const handleConfirmDelete = () => {
    handleDeleteData(`/equipments/${row.equipmentId}`, setDeleteConfirmOpen);
  };

  const theme = getEquipmentStatusTheme(row);

  // Card themes classes
  const themeStyles = {
    green: {
      borderLeft: "border-l-4 border-l-emerald-500",
      iconBg: "bg-emerald-50 text-emerald-600",
    },
    blue: {
      borderLeft: "border-l-4 border-l-blue-500",
      iconBg: "bg-blue-50 text-blue-600",
    },
    red: {
      borderLeft: "border-l-4 border-l-rose-500",
      iconBg: "bg-rose-50 text-rose-600",
    },
    purple: {
      borderLeft: "border-l-4 border-l-indigo-500",
      iconBg: "bg-indigo-50 text-indigo-600",
    }
  };

  const styles = themeStyles[theme] || themeStyles.purple;
  const borderLeftClass = styles.borderLeft;
  const iconBgClass = styles.iconBg;

  return (
    <div className={`relative border border-y-gray-200/50 border-r-gray-200/50 border-l-0 shadow-[0_4px_20px_rgba(0,0,0,0.05)] hover:shadow-[0_10px_30px_rgba(0,0,0,0.08)] transition-all duration-300 rounded-2xl bg-white p-4 flex flex-col overflow-hidden ${borderLeftClass}`}>
      {/* Top Header Row of Card */}
      <div className="flex justify-between items-center gap-4 w-full">
        <div className="flex items-center gap-6 flex-1 min-w-0">
          <div className="flex items-center gap-3 shrink-0">
            {/* Styled Avatar or Icon */}
            <div className={`lg:w-12 lg:h-12 rounded-xl flex items-center justify-center font-bold text-lg select-none ${iconBgClass}`}>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <div className="flex flex-col">
              <div className="flex items-center gap-1">
                <Link
                  to={`/maintenance/equipment/view/${row.equipmentId}`}
                  state={{ data: row, pageIndex, searchQuery }}
                  className="text-gray-800 font-bold hover:underline text-[14px] truncate max-w-[140px]"
                  title={row.equipmentType}
                >
                  {row.equipmentType || "N/A"}
                </Link>
                <CopyButton text={row.equipmentType} />
              </div>
              <span className="text-[11px] text-gray-500 font-semibold mt-0.5">
                ID: {row.equipmentId || "N/A"}
              </span>
            </div>
          </div>
        </div>

        {/* Right Side: Status Badge */}
        <div className="shrink-0 scale-95 origin-right">
          {renderEquipmentStatus(row)}
        </div>
      </div>

      {/* Brand & Manufacturer Info */}
      <div className="flex items-center gap-2 mt-2 px-1">
        {row.brand && (
          <span className="text-[11px] text-gray-500 font-medium">
            Brand: {row.brand}
          </span>
        )}
        {row.manufacturer && (
          <span className="text-gray-300">•</span>
        )}
        {row.manufacturer && (
          <span className="text-[11px] text-gray-500 font-medium truncate max-w-[150px]">
            {row.manufacturer}
          </span>
        )}
      </div>

      {/* Middle Panel Grid */}
      <div className="rounded-xl py-2 flex flex-col sm:flex-row justify-between items-start lg:items-center gap-2 mt-4 bg-gray-50/50 lg:px-4">
        {/* Quantity */}
        <div className="flex flex-col gap-0.5 flex-1 pl-2">
          <span className="text-[10px] font-semibold text-gray-500 uppercase select-none">Quantity</span>
          <span className="text-[12px] font-bold text-gray-700 leading-tight">
            {row.quantity || "0"} {row.unit || ""}
          </span>
        </div>

        {/* Divider */}
        <Divider />
        {/* Warrenty */}
        <div className="flex flex-col gap-0.5 flex-1 pl-2">
          <span className="text-[10px] font-semibold text-gray-500 uppercase select-none">Warranty Exp</span>
          <span className="text-[12px] font-bold text-gray-700 leading-tight truncate">
            {row.warrentyExpDate ? moment(row.warrentyExpDate).format("DD-MMM-YYYY") : "--"}
          </span>
        </div>

        {/* Divider */}
        <Divider />

        {/* Calibration Expiration */}
        <div className="flex flex-col gap-0.5 flex-1 pl-2">
          <span className="text-[10px] font-semibold text-gray-500 uppercase select-none">Calib Exp</span>
          <span className="text-[12px] font-bold text-gray-700 leading-tight">
            {row.calibExpDate ? moment(row.calibExpDate).format("DD-MMM-YYYY") : "--"}
          </span>
        </div>
      </div>

      {/* Action Buttons Row */}
      <div className="flex items-center justify-between border-t border-gray-200 pt-3 mt-4">
        {/* View Details */}
        <Link
          to={`/maintenance/equipment/view/${row.equipmentId}`}
          state={{ data: row, pageIndex, searchQuery }}
          className="border border-gray-200 text-gray-600 hover:bg-gray-50 px-2.5 sm:px-3.5 py-1.5 rounded-lg transition-all duration-200 flex items-center gap-1.5 text-xs font-semibold whitespace-nowrap"
          title="View details"
        >
          <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
          <span className="hidden sm:inline">View Details</span>
        </Link>

        {/* Edit & Delete */}
        <div className="flex items-center gap-1.5">
          {CheckEditAction(row, "Equipment", canEdit) ? (
            <Link
              to={`/maintenance/equipment/edit/${row.equipmentId}`}
              state={{ data: row, from: "/maintenance/equipment", pageIndex, searchQuery }}
              className="border border-sky-100 text-[#0ea5e9] hover:bg-sky-50 px-2.5 sm:px-3.5 py-1.5 rounded-lg transition-all duration-200 flex items-center gap-1.5 text-xs font-semibold whitespace-nowrap"
              title="Edit"
            >
              <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-5M18.5 2.5a2.121 2.121 0 113 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
              <span className="hidden sm:inline">Edit</span>
            </Link>
          ) : null}

          {CheckDeleteAction(row, canDelete) ? (
            <button
              onClick={() => setDeleteConfirmOpen(true)}
              className="border border-red-100 text-red-500 hover:bg-red-50 px-2.5 sm:px-3.5 py-1.5 rounded-lg transition-all duration-200 flex items-center gap-1.5 text-xs font-semibold whitespace-nowrap"
              title="Delete"
            >
              <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              <span className="hidden sm:inline">Delete</span>
            </button>
          ) : null}
        </div>
      </div>

      {/* Delete Warning Modal */}
      {deleteConfirmOpen && (
        <ModalContainer
          visiable={deleteConfirmOpen}
          title="Warning"
          content={`Are you sure you want to remove this Equipment record?`}
          handleClick={handleConfirmDelete}
          closeModal={() => setDeleteConfirmOpen(false)}
        />
      )}
    </div>
  );
};

// Main EquipmentTable Component
const EquipmentTable = ({
  data = [],
  total = 0,
  pageIndex,
  setPageIndex,
  dataCount,
  setDataCount,
  searchQuery,
  fetcher,
  dataLoading = false,
  viewMode = "table",
}) => {
  const headers = [
    { title: "ID", width: "10%" },
    { title: "Type", width: "60%" },
    { title: "Brand", width: "20%" },
    { title: "Action", align: "center", width: "10%" },
  ];

  return (
    <PremiumTable
      data={data}
      total={total}
      pageIndex={pageIndex}
      setPageIndex={setPageIndex}
      dataCount={dataCount}
      setDataCount={setDataCount}
      viewMode={viewMode}
      headers={headers}
      renderRow={(row, idx) => (
        <EquipmentTableRow
          row={row}
          pageIndex={pageIndex}
          searchQuery={searchQuery}
          fetcher={fetcher}
          index={idx}
        />
      )}
      renderCard={(row, idx) => (
        <EquipmentCard
          row={row}
          pageIndex={pageIndex}
          searchQuery={searchQuery}
          fetcher={fetcher}
          index={idx}
        />
      )}
      dataLoading={dataLoading}
    />
  );
};

export default EquipmentTable;
